"use client";

/**
 * The customer's last known GPS position, kept so the search API can label each
 * pickup point with how far away it is by road.
 *
 * The browser already asks for location to work out which city someone is in
 * (locationHelper.autoDetectCity), then throws the coordinates away. This keeps
 * them, under their own localStorage key, following the same pattern as
 * anonymousId: one small module owning one key, read by utils/api.jsx.
 *
 * WHY NOT PASS IT AT EACH CALL SITE: four places build a search/index payload,
 * and utils/api.jsx dedups those calls on the EXACT JSON body. A payload
 * carrying coordinates from one call site and not another produces two
 * different keys, so the dedup stops collapsing the home-form/search-page pair
 * it exists to absorb — and the two calls would then return different answers
 * for the same search. Injecting centrally is what keeps every payload
 * identical.
 *
 * Coordinates are ROUNDED to ~1 km before storage. The grid they feed is built
 * in ~1.1 km squares, so finer precision buys nothing, and it means neither the
 * request body nor localStorage ever holds a person's exact location.
 *
 * Expires: a stale position is worse than none. Someone who searched in Chennai
 * last week and is now in Goa must not be told a Chennai branch is "5 km away".
 */
const STORAGE_KEY = "sukuto_pos";

/** ~1.1 km, matching the grid's square size. */
const PRECISION = 2;

/** After this the position is treated as unknown rather than trusted. */
const MAX_AGE_MS = 6 * 60 * 60 * 1000; // 6 hours

export const setCustomerPosition = (lat, lng) => {
  try {
    if (typeof window === "undefined") return;
    if (!Number.isFinite(Number(lat)) || !Number.isFinite(Number(lng))) return;
    if (Number(lat) === 0 || Number(lng) === 0) return;
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        lat: Number(Number(lat).toFixed(PRECISION)),
        lng: Number(Number(lng).toFixed(PRECISION)),
        at: Date.now(),
      })
    );
  } catch {
    // Private mode / storage disabled — distances just stay unavailable.
  }
};

export const getCustomerPosition = () => {
  try {
    if (typeof window === "undefined") return null;
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Number.isFinite(parsed.lat) || !Number.isFinite(parsed.lng)) return null;
    if (!Number.isFinite(parsed.at) || Date.now() - parsed.at > MAX_AGE_MS) return null;
    return { lat: parsed.lat, lng: parsed.lng };
  } catch {
    return null;
  }
};

export const clearCustomerPosition = () => {
  try {
    if (typeof window !== "undefined") window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};
